import { useEffect, useRef } from "react";
import { Cookie, LocalStorage } from "../services/storage";
import {
	REDIRECT_URI,
	REQUEST_NUMBER,
	LOGIN_COMPLETED,
	ADSSO_TOKEN_EXPIRY,
	ADSSO_REDIRECT_COUNT,
} from "../constants/storage";

import {
	DISABLED_FEATURES,
	useFeatureToggle,
} from "../utils/feature-toggle/useFeatureToggle";

import { ROUTES } from "../configs/routes";
import { TURKEY_SA_COUNTRY_CODE } from "../constants/countryDefaults";
import { useSiteSettingsHook } from "./site-settings-hooks";
import { useAdssoLoginGuard } from "./adsso-login-guard-hooks";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "./query-hooks";
import { ApplicationState } from "../store";
import { UserState } from "../store/reducers/user-reducer";
import { useSelector } from "react-redux";
import { useLayout } from "./layout-hooks";
import i18next from "i18next";
import { useUserHooks } from "./user-hooks";
import { useMaintenance } from "./maintenance-page-hooks";
import { useLocale } from "./locale-hooks";
import { SystemState } from "../store/reducers/site-reducer";
import { useLoginOptionHooks } from "./login-option-hooks";
import GoogleAnalyticsV2 from "../utils/GoogleAnalyticsV2";
import { useAuthProvider } from "./useAuthProvider";
import { AuthStatus } from "../types/auth";

export const useLayoutStartup = () => {
	const { pathname } = useLocation();
	const history = useHistory();
	const query = useQuery();
	const { isDisabled } = useFeatureToggle();
	const { loadSystemProperties } = useSiteSettingsHook();
	const { ssoLoginRedirect, verifyAdssoToken } = useAdssoLoginGuard();
	const { removeEmailLoginSession } = useLoginOptionHooks();
	const { removeSelectedCountry, getUserCountry, setSelectedCountry } =
		useLayout();
	const { isLoading: isMaintenanceLoading } = useMaintenance();
	const { getUserData, fetchProfile, loadUserCountryCode } = useUserHooks();
	// Holds a profile request started before site settings are available
	const profileRequest = useRef<Promise<any> | null>(null);
	const { loadLocale } = useLocale();

	//Redux Hooks
	const { isLoading: siteSettingIsLoading, sysProps } = useSelector<
		ApplicationState,
		SystemState
	>((state) => state.system);
	const { userData, userCountryCode } = useSelector<
		ApplicationState,
		UserState
	>((state) => state.user);
	const isUserLoggedIn = useSelector<ApplicationState, boolean>(
		(state) => state.auth.isLoggedIn
	);

	const authStatus = useSelector<ApplicationState, AuthStatus>(
		(state) => state.auth.status
	);

	const { detectAuthType, setAuthStatus } = useAuthProvider();

	// hasPageAccess should only check i18n initialization and loading state
	// maintenance routing is handled by Router.tsx route guard
	const hasPageAccess = i18next.isInitialized && !isMaintenanceLoading;

	const loadSiteSettings = () => {
		if (!isDisabled(DISABLED_FEATURES.SITE_SETTINGS)) loadSystemProperties();
	};

	// The ADSSO token is HttpOnly, so the backend is asked instead of reading a
	// cookie. Only a confirmed "authenticated" keeps the user here; an expired
	// token (403) and any transport failure both fall through to the redirect.
	//
	// Fast path: if there is no cached ADSSO_TOKEN_EXPIRY in localStorage AND we
	// haven't already redirected in this tab session, redirect to SSO immediately
	// without an API call — the cache is only set after a successful verify, so
	// its absence means no prior verified session exists.
	//
	// Loop prevention: a sessionStorage counter (ADSSO_REDIRECT_COUNT) tracks how
	// many times this tab has redirected to SSO. On return from SSO the counter is
	// > 0, so instead of redirecting again we call verifyAdssoToken() — the
	// backend has now set the AD_ATKN cookie and the verify will succeed. If verify
	// still fails after MAX_RETRIES, we stop redirecting to avoid an infinite loop.
	const MAX_ADSSO_RETRIES = 2;

	const redirectIfNoADSSOToken = async () => {
		if (isDisabled(DISABLED_FEATURES.ADSSO_SITE_RESTRICTION)) return;

		const cachedExpiry = LocalStorage.get(ADSSO_TOKEN_EXPIRY);
		const redirectCount = Number(
			sessionStorage.getItem(ADSSO_REDIRECT_COUNT) || "0"
		);

		// Fast path: no cache and never redirected in this tab → redirect immediately
		if (!cachedExpiry && redirectCount === 0) {
			sessionStorage.setItem(ADSSO_REDIRECT_COUNT, "1");
			ssoLoginRedirect();
			return;
		}

		// Either has a cache (normal verify) or just came back from SSO
		// (redirectCount > 0) — call verifyAdssoToken which checks the cache
		// first and only hits the API when the cache is absent or expired.
		const isAuthenticated = await verifyAdssoToken();
		if (isAuthenticated) {
			sessionStorage.removeItem(ADSSO_REDIRECT_COUNT);
			return;
		}

		// Verify failed — redirect to SSO if we haven't exceeded max retries
		if (redirectCount < MAX_ADSSO_RETRIES) {
			sessionStorage.setItem(ADSSO_REDIRECT_COUNT, String(redirectCount + 1));
			ssoLoginRedirect();
		} else {
			// Max retries exceeded — stop to prevent infinite loop
			sessionStorage.removeItem(ADSSO_REDIRECT_COUNT);
		}
	};

	const handleOldGRPSRedirect = () => {
		if (pathname === ROUTES.OLD_ABOUT_GRPS) {
			history.replace(ROUTES.ABOUT_GRPS);
		}
	};

	const handleRemoveTurkeySAUserCC = () => {
		if (userData?.countryCode === TURKEY_SA_COUNTRY_CODE && isUserLoggedIn) {
			removeSelectedCountry();
		}
	};

	useEffect(() => {
		// Wait for maintenance config to load before making any API calls
		if (isMaintenanceLoading) return;

		// Load locale immediately after maintenance config is loaded
		// This ensures i18next has the correct language set before any page renders (including MaintenancePage)
		loadLocale();

		// Send the user through ADSSO when the backend says the token is missing
		// or expired
		redirectIfNoADSSOToken();
		// Load site-settings
		loadSiteSettings();
		// Handle user redirection to new GRPS Page when user access old link.
		handleOldGRPSRedirect();
		// Handle removal of user selected country code when user logged in with TR SA-Countrycode
		handleRemoveTurkeySAUserCC();
	}, [isMaintenanceLoading]);

	// Settle auth status as early as possible. Route guards block rendering while
	// it is "unknown", so this has to run for every visitor, not just ones
	// returning from a login.
	useEffect(() => {
		if (query.get("success")) {
			sessionStorage.setItem(LOGIN_COMPLETED, "SA");
			// Strip ?success so a refresh doesn't re-trigger the profile fetch.
			history.replace({ pathname, search: "" });
		}

		// A login in flight is resolved by the profile fetch below. Start that
		// request now rather than letting it queue behind the maintenance and
		// site-settings round trips — it needs neither to be issued, only to be
		// interpreted. Only done for a just-completed login, where a profile call
		// is certain to be needed and cannot be served from cache.
		if (sessionStorage.getItem(LOGIN_COMPLETED)) {
			profileRequest.current = fetchProfile();
			// Rejection is handled where the promise is awaited; this only keeps
			// the browser from reporting it as unhandled in the meantime.
			profileRequest.current.catch(() => {});
			return;
		}
		// Otherwise promote from persisted user data, or settle as anonymous so
		// guards stop waiting.
		if (!detectAuthType().isLoggedIn) setAuthStatus("anonymous");
	}, []);

	// Send the user to the deep link they originally asked for, once auth
	// resolves. REDIRECT_URI carries the request number that getRedirectUrl
	// strips off before handing the path to the SA return URL.
	useEffect(() => {
		if (authStatus !== "authenticated") return;

		const target = Cookie.get(REDIRECT_URI);
		if (!target) return;

		Cookie.remove(REDIRECT_URI);
		Cookie.remove(REQUEST_NUMBER);

		// getRedirectUrl builds this from a REQUEST_NUMBER cookie that may be gone
		if (target.includes("undefined") || target === pathname) return;
		history.replace(target);
	}, [authStatus]);

	useEffect(() => {
		// Initialize Google Analytics
		GoogleAnalyticsV2.initialize(globalThis.location.host); // Note: Page view tracking is now handled by AnalyticsTracker component

		// to avoid duplicate page view events
	}, [pathname]);

	// Handle loading of locales and redirection of user to china local site when country code is CN
	useEffect(() => {
		if (!userCountryCode) return;
		loadLocale();

		const country = getUserCountry();
		setSelectedCountry(country);
	}, [userCountryCode]);

	// getUserCountryCode and getUserData (profile API) when user logged in.
	useEffect(() => {
		// Wait for both site settings and maintenance config to load
		if (siteSettingIsLoading || isMaintenanceLoading) return;

		// Detect auth type on mount to populate auth slice from persisted/localStorage state
		detectAuthType();

		loadUserCountryCode();
		removeEmailLoginSession(); //cleanup

		// Call profile API in these scenarios:
		// 1. Login just completed (EMAIL or SA): LOGIN_COMPLETED flag set in sessionStorage
		// 2. Hard reload with existing session: USERDATA cached → isUserLoggedIn true (validate)
		const isLoginJustCompleted = !!sessionStorage.getItem(LOGIN_COMPLETED);
		if (!isUserLoggedIn && !isLoginJustCompleted) return;

		(async () => {
			const result = await getUserData(profileRequest.current ?? undefined);

			if (result === "skipped") {
				// SA_COUNTRY isn't populated yet, so the request never ran. Keep the
				// flag: this effect re-runs once site settings land and completes the
				// login then. Resolving "anonymous" here would bounce a user who has
				// just authenticated back to the login prompt.
				if (isLoginJustCompleted) return;
				if (!detectAuthType().isLoggedIn) setAuthStatus("anonymous");
				return;
			}

			// Consumed — a later re-run must not reuse a resolved response.
			// Clear the flag first so a later re-run won't try to refetch.
			sessionStorage.removeItem(LOGIN_COMPLETED);
			profileRequest.current = null;
		})();
		// SA_COUNTRY is in the deps because siteSettingIsLoading starts false —
		// it means "not started" as well as "done", so it alone lets this run
		// before system properties exist. We only refetch if LOGIN_COMPLETED
		// is still set, so clearing it above prevents unwanted refetches.
	}, [siteSettingIsLoading, isMaintenanceLoading, sysProps.SA_COUNTRY]);

	return { hasPageAccess };
};
